import axios from 'axios';
import { DateTime } from 'luxon';

import { getCountryUIConfig } from '../../../contexts/country-config-context/country-ui-config';
import { DEFAULT_DEMO_FULFILLMENT_PLAN_ID } from '../../../demos/demo-orders';
import { FeatureFlagDeclarations } from '../../../feature-flags/feature-flag-declarations';
import { getContent as getLookerContent } from '../../../pages/insights/looker/api/looker-embed-api';
import * as IntegrationsApi from '../../../pages/settings/api/integrations-api';
import { getSellerIORs } from '../../../services/iors/api';
import { getSellerServiceLevels } from '../../../services/service-levels/api';
import { ShippingServiceService } from '../../../services/shipping-service-service';
import { getSellerTaxIdentifiers } from '../../../services/tax-identifiers/api';
import * as GridConfigApi from '../../../store/grid-config.api';
import * as LabelQueApi from '../../../store/label-que/label-que.api';
import { getSettingsRaw } from '../../../store/seller/seller.api';
import { hydratePackageIdForPackageTypes, mergePackages, mergeServices, mergeLabelProviders } from '../../../store/seller/seller.epic';
import { getWalletAndEligibilityData } from '../../../wallet/utilities/wallet-service';

const cookielessLookerEmbedFlag = FeatureFlagDeclarations.enableCookielessLookerEmbed;
const isCookielessLookerEmbedEnabled = () =>
  Boolean(window.ldClient?.variation(cookielessLookerEmbedFlag.key, cookielessLookerEmbedFlag.defaultValue));

const isErrorResponse = (response) => {
  if (!response?.status || response.status === 200) return false;
  if (response.status === 204) return true;
  return true;
};

export const phase2Call = (data) => {
  // oxlint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const { defaultCurrency, subscriptionPlanInfo, homeCountry } = data.input.seller;
      const { isVendorOr3PL } = subscriptionPlanInfo;

      const { userId: currentUserId } = data.input.currentUser;

      const fromProcessDate = DateTime.now().minus({ days: 3 }).toFormat('MM/dd/yyyy');

      const promises = [
        axios.get('/seller/shipfrom'),
        axios.get('/seller/packagetypes'),
        axios.get('/seller/labelproviders'),
        axios.get('/freight/seller-provider').catch(() => ({ data: [] })),
        axios.get('/seller/services'),
        axios.get('/seller/stores'),
        axios.get('/presets'),
        axios.get('/emailtemplate/delivery'),
        axios.get('/emailtemplate/shipment'),
        axios.get('/packingslip'),
        axios.get('/user/setting'),
        axios.get('/tag'),
        axios.get('/packageset'),
        getSettingsRaw(),
        GridConfigApi.getAllGridConfigsRaw(),
        axios.get('/return/reasons'),
        axios.get('/seller/fulfillmentproviders?showInactive=true'),
        axios.get('/seller/fulfillmentservices'),
        LabelQueApi.getLabelQueBatchesRaw(currentUserId, fromProcessDate, 25),
        axios.get('/product/profiles'),
        axios.get('/IntegrationsShippingPlatform/ShippingOptions'),
        axios.get('/productCategory'),
        getWalletAndEligibilityData(defaultCurrency),
        axios.get('/seller/sellerBrands'),
        axios.get('/seller/pendingFulfillmentPartners'),
        IntegrationsApi.getShipperHQConnectedStoreGuids(),
        getSellerTaxIdentifiers({ includeInactive: true }),
        getSellerIORs(),
        getSellerServiceLevels().catch(() => []),
        axios.get('/seller/settings/ups_invoiced').catch(() => false),
        axios.get(`/country/${homeCountry}/features`),
        axios.get('/IntegrationsPlatform/FulfillmentOptions'),
        axios.get('/Demos/existingDemoOrder').catch(() => ({ data: DEFAULT_DEMO_FULFILLMENT_PLAN_ID })),
        axios.get('/seller/fulfillmentproviderwarehouses'),
      ];

      if (isVendorOr3PL) {
        promises.push(axios.get('/carriersharing/partner/services'));
        promises.push(axios.get('/carriersharing/partner/labelproviders'));
        promises.push(axios.get('/carriersharing/partner/seller/services'));
        promises.push(axios.get('/carriersharing/partner/packagetypes'));
      }

      const lookerContentPromise = isCookielessLookerEmbedEnabled() ? getLookerContent().catch(() => null) : Promise.resolve(null);

      const promiseData = await Promise.all(promises);
      const errors = promiseData.filter((response) => isErrorResponse(response)).map((response) => response.statusText);
      if (errors.length > 0) {
        throw new Error(errors.join(','));
      }
      const [
        shipFromResponse,
        packageTypesResponse,
        labelProvidersResponse,
        freightSellerProvidersResponse,
        servicesResponse,
        storesResponse,
        presetsResponse,
        deliveryTemplatesResponse,
        shipmentTemplatesResponse,
        packingSlipsResponse,
        userSettingsResponse,
        tagsResponse,
        packageSetResponse,
        sellerSettingsResponse,
        gridConfigsResponse,
        returnReasonsResponse,
        fulfillmentProvidersResponse,
        fulfillmentServicesResponse,
        labelQueueBatchesResponse,
        productProfilesResponse,
        shippingOptionsResponse,
        productCategoryResponse,
        walletResponse,
        sellerBrandsResponse,
        pendingFulfillmentPartnersResponse,
        shipperHQConnectedStoreGuidsResponse,
        taxIdentifierResponse,
        iorResponse,
        serviceLevelsResponse,
        hasViewedUpsInvoicedModalResponse,
        countryFeatureResponse,
        fulfillmentOptionsResponse,
        hasDemoOrderResponse,
        fulfillmentProviderWarehousesResponse,
        carrierSharingPartnerServicesResponse,
        carrierSharingPartnerLabelProvidersResponse,
        carrierSharingPartnerSellerServicesResponse,
        carrierSharingPartnerPackageTypesResponse,
      ] = promiseData;

      const services = mergeServices(servicesResponse.data || [], carrierSharingPartnerSellerServicesResponse?.data || []);
      const mergedLabelProviders = mergeLabelProviders(
        labelProvidersResponse.data || [],
        carrierSharingPartnerLabelProvidersResponse?.data || [],
      );

      const sellerFreightProviders = freightSellerProvidersResponse?.data ?? [];

      const countryUIConfig = getCountryUIConfig(homeCountry);
      const countryConfig = [...countryUIConfig, ...(countryFeatureResponse.data || [])];
      const newData = {
        shipFroms: shipFromResponse.data || [],
        packages: hydratePackageIdForPackageTypes(
          mergePackages(packageTypesResponse.data || [], carrierSharingPartnerPackageTypesResponse?.data || []),
        ),
        labelProviders: mergedLabelProviders,
        sellerFreightProviders,
        services: ShippingServiceService.hydrateSellerLabelProviderServices(
          services,
          labelProvidersResponse.data || [],
          shippingOptionsResponse.data || [],
        ),
        extendedServices: ShippingServiceService.hydrateSellerLabelProviderServices(
          services,
          mergedLabelProviders,
          shippingOptionsResponse.data || [],
        ),
        stores: storesResponse.data || [],
        presets: presetsResponse.data || [],
        deliveryTemplates: deliveryTemplatesResponse.data || [],
        shipmentTemplates: shipmentTemplatesResponse.data || [],
        packingSlips: packingSlipsResponse.data || [],
        userSettings: userSettingsResponse.data || [],
        tags: tagsResponse.data || [],
        packageSets: packageSetResponse.data || [],
        returnReasons: returnReasonsResponse.data || [],
        settings: sellerSettingsResponse.data || [],
        fulfillmentProviders: fulfillmentProvidersResponse.data || [],
        fulfillmentProviderWarehouses: fulfillmentProviderWarehousesResponse.data || [],
        fulfillmentServices: fulfillmentServicesResponse.data || [],
        inventoryWarehouses: [], // Will be loaded asynchronously after app renders
        inventoryWarehousesLocations: [], // Will be loaded asynchronously after app renders
        wallets: walletResponse || [],
        sellerBrands: sellerBrandsResponse.data || [],
        pendingFulfillmentPartners: pendingFulfillmentPartnersResponse.data || [],
        shipperHQConnectedStores: shipperHQConnectedStoreGuidsResponse || [],
        serviceCollections: [], // initialize the value here, we will make the request for it in the app init epic
        countryConfig,
        fulfillmentProviderOptions: fulfillmentOptionsResponse.data || [],
        demoFulfillmentPlanId: hasDemoOrderResponse.data || DEFAULT_DEMO_FULFILLMENT_PLAN_ID,
        allGridConfigs: gridConfigsResponse || [],
        labelQueBatches: labelQueueBatchesResponse?.data?.results?.pageData || [],
        productProfiles: productProfilesResponse.data || [],
        productCategories: productCategoryResponse.data || [],
        taxIdentifiers: taxIdentifierResponse || {},
        iors: iorResponse || {},
        sharedServices: carrierSharingPartnerServicesResponse?.data || [],
        serviceLevels: serviceLevelsResponse || [],
        hasViewedUpsInvoicedModal: hasViewedUpsInvoicedModalResponse.data ?? false,
        lookerContent: await lookerContentPromise,
      };
      resolve(newData);
    } catch (e) {
      reject(e);
    }
  });
};
